import { containsCoordinate } from "ol/extent"
import type { Extent } from "ol/extent"
import { createXYZ } from "ol/tilegrid"
import { Data } from "rete/types/core/data"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../modelling/tile_grid"
import type { DatasetLayer, Layer, ModelOutputLayer } from "../state"
import { resolveCategoryColor } from "../layers/categorical_layer_colors"
import {
  isCategoricalTableRowView,
  numericDisplayValueForCategoricalCell,
} from "../layers/categorical_layer_view"
import type { DatasetCache, ModelOutputCache } from "./map_view"
import type { MapStackSlot } from "./map_layer_stack"
import { categoryGroupKeyForLabel, type TableProbeMatch } from "../editor/model_detail"

export type MapProbeSample = {
  layerName: string
  layerKind: "model" | "dataset"
  displayValue: string
  nearest: boolean
  unavailable?: boolean
  /** CSS color for categorical value text (from layer palette). */
  valueColor?: string
  /** Normalized label key for highlighting the Detail panel category row. */
  categoryGroupKey?: string
  /**
   * Map-projection (EPSG:3857) extent of the underlying grid cell that this
   * sample was read from, in the form `[minX, minY, maxX, maxY]`. Used by
   * `map_view` to draw the "you clicked this pixel" highlight rectangle on
   * the map. Only populated for samples whose grid is loaded AND whose cell
   * is inside the grid (i.e. `unavailable === false && nearest === false`).
   */
  cellExtent?: Extent
}

type TileGridLike = BooleanTileGrid | NumericTileGrid | CategoricalTileGrid

function coordinateToGridTileXY(
  coordinate: [number, number],
  grid: { zoom: number; x: number; y: number; width: number; height: number }
): { tileX: number; tileY: number; inBounds: boolean; nearest: boolean } {
  const tg = createXYZ()
  const z = grid.zoom
  const tileCoord = tg.getTileCoordForCoordAndZ(coordinate, z)
  const extent = tg.getTileCoordExtent(tileCoord)
  const spanX = extent[2] - extent[0]
  const spanY = extent[3] - extent[1]
  const relX = spanX > 0 ? (coordinate[0] - extent[0]) / spanX : 0
  const relY = spanY > 0 ? (extent[3] - coordinate[1]) / spanY : 0
  let tileX = tileCoord[1] + relX
  let tileY = tileCoord[2] + relY

  const minX = grid.x
  const maxX = grid.x + grid.width
  const minY = grid.y
  const maxY = grid.y + grid.height

  const inBounds = tileX >= minX && tileX < maxX && tileY >= minY && tileY < maxY
  let nearest = false
  if (!inBounds) {
    nearest = true
    const epsilon = 1e-6
    tileX = Math.min(Math.max(tileX, minX), maxX - epsilon)
    tileY = Math.min(Math.max(tileY, minY), maxY - epsilon)
  }

  return { tileX, tileY, inBounds, nearest }
}

function formatTileGridValue(grid: TileGridLike, raw: number): string {
  if (grid instanceof BooleanTileGrid) {
    if (raw === 255 || Number.isNaN(raw as number)) return "No data"
    return raw ? "Yes" : "No"
  }
  if (grid instanceof NumericTileGrid) {
    if (typeof raw !== "number" || Number.isNaN(raw)) return "No data"
    const unit = grid.properties?.unit
    const area = grid.properties?.area
    const formatted = Number.isInteger(raw) ? String(raw) : raw.toPrecision(4)
    // Match the convention used in the analysis panel and histogram axes:
    // when both a value unit (e.g. "t") and a per-area unit (e.g. "ha") are
    // set, the combined unit is rendered as "unit/area" — e.g. "5 t/ha".
    // `"NA"` / `"na"` are the placeholder values written by raster sources
    // (see e.g. `isric_soil_filters.ts`) for "this property doesn't apply",
    // so treat them as absent.
    const hasUnit = !!unit && unit !== "NA" && unit !== "na"
    const hasArea = !!area && area !== "NA" && area !== "na"
    if (hasUnit && hasArea) return `${formatted} ${unit}/${area}`
    if (hasUnit) return `${formatted} ${unit}`
    return formatted
  }
  if (grid instanceof CategoricalTileGrid) {
    if (raw === 255) return "No data"
    const label = grid.labels?.get(raw)
    if (label) return label
    return `Category ${raw}`
  }
  return String(raw)
}

function categoricalValueColor(
  layer: ModelOutputLayer | DatasetLayer,
  categoryValue: number,
  label?: string
): string | undefined {
  if (categoryValue === 255) return undefined
  const rgba = resolveCategoryColor(categoryValue, label, {
    colors: layer.colors,
    colorByLabel: layer.colorByLabel,
  })
  return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] ?? 1})`
}

export function sampleTileGridAtCoordinate(
  grid: TileGridLike,
  coordinate: [number, number]
): { displayValue: string; nearest: boolean; categoryValue?: number } {
  const { tileX, tileY, nearest } = coordinateToGridTileXY(coordinate, grid)
  if (grid instanceof BooleanTileGrid) {
    const raw = grid.get(tileX, tileY, grid.zoom)
    return { displayValue: raw ? "Yes" : "No", nearest }
  }
  const raw = grid.get(tileX, tileY, grid.zoom) as number
  return {
    displayValue: formatTileGridValue(grid, raw),
    nearest,
    categoryValue: grid instanceof CategoricalTileGrid ? raw : undefined,
  }
}

/**
 * Map-projection extent of the single grid cell that contains `coordinate`,
 * at the grid's native zoom. Returns `null` if the coordinate falls outside
 * the grid's bounds, since "highlight the cell I clicked" only makes sense
 * when the click was actually on a cell.
 *
 * The math mirrors `coordinateToGridTileXY` — one grid cell at zoom z equals
 * one XYZ tile at zoom z (this codebase stores one value per XYZ tile), so
 * the cell's geographic extent is just the tile's extent from `createXYZ()`.
 */
export function cellExtentForCoordinate(
  grid: TileGridLike,
  coordinate: [number, number]
): Extent | null {
  const { tileX, tileY, inBounds } = coordinateToGridTileXY(coordinate, grid)
  if (!inBounds) return null
  const tg = createXYZ()
  return tg.getTileCoordExtent([grid.zoom, Math.floor(tileX), Math.floor(tileY)])
}

function cacheKeyForLayer(layer: ModelOutputLayer | DatasetLayer): number {
  return layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
}

export function collectMapProbeSamples(
  coordinate: [number, number],
  mapLayerStack: MapStackSlot[],
  projectLayersById: Record<number, Layer>,
  modelOutputCache: ModelOutputCache,
  datasetCache: DatasetCache,
  model: Data | null = null
): MapProbeSample[] {
  const samples: MapProbeSample[] = []

  for (let i = mapLayerStack.length - 1; i >= 0; i--) {
    const slot = mapLayerStack[i]
    if (slot.kind !== "project") continue

    const layerDef = projectLayersById[slot.layerId]
    if (!layerDef) continue
    if (layerDef.type !== "ModelOutputLayer" && layerDef.type !== "DatasetLayer") continue
    if (layerDef.type === "DatasetLayer" && layerDef.deleted) continue

    const cacheId = cacheKeyForLayer(layerDef)
    const cache = layerDef.type === "ModelOutputLayer" ? modelOutputCache : datasetCache
    const grid = cache[cacheId]

    if (!grid) {
      samples.push({
        layerName: layerDef.name,
        layerKind: layerDef.type === "ModelOutputLayer" ? "model" : "dataset",
        displayValue: "Loading…",
        nearest: false,
        unavailable: true,
      })
      continue
    }

    const { displayValue, nearest, categoryValue } = sampleTileGridAtCoordinate(grid, coordinate)
    const rowViewNumeric =
      grid instanceof CategoricalTileGrid &&
      isCategoricalTableRowView(layerDef) &&
      categoryValue !== undefined
        ? numericDisplayValueForCategoricalCell(layerDef, grid, categoryValue, model)
        : null
    const effectiveDisplayValue = rowViewNumeric ?? displayValue
    const valueColor =
      grid instanceof CategoricalTileGrid &&
      categoryValue !== undefined &&
      !rowViewNumeric
        ? categoricalValueColor(layerDef, categoryValue, displayValue !== "No data" ? displayValue : undefined)
        : undefined
    const isCategorical =
      grid instanceof CategoricalTileGrid &&
      categoryValue !== undefined &&
      categoryValue !== 255 &&
      displayValue !== "No data" &&
      !rowViewNumeric
    const cellExtent = nearest ? undefined : cellExtentForCoordinate(grid, coordinate) ?? undefined
    samples.push({
      layerName: layerDef.name,
      layerKind: layerDef.type === "ModelOutputLayer" ? "model" : "dataset",
      displayValue: effectiveDisplayValue,
      nearest,
      valueColor,
      categoryGroupKey: isCategorical ? categoryGroupKeyForLabel(displayValue) : undefined,
      cellExtent,
    })
  }

  return samples
}

/**
 * Picks the cell extent we should outline on the map for the popup. Walks
 * `samples` in render order (topmost layer first — `collectMapProbeSamples`
 * iterates the stack top-down) and returns the first cell that's loaded AND
 * in-bounds. We highlight the topmost rendered cell because that's the data
 * the user actually saw under their cursor when clicking.
 *
 * Returns `null` when nothing usable is available — the caller should clear
 * any existing highlight in that case.
 */
export function probeCellExtentFromSamples(samples: MapProbeSample[]): Extent | null {
  for (const s of samples) {
    if (s.unavailable || s.nearest) continue
    if (s.cellExtent) return s.cellExtent
  }
  return null
}

/** First categorical label at the probe point (for Detail panel highlight). */
export function categoryGroupKeyFromProbeSamples(samples: MapProbeSample[]): string | null {
  const hit = samples.find(s => !s.unavailable && !s.nearest && s.categoryGroupKey)
  return hit?.categoryGroupKey ?? null
}

/** False when outside project extent, still loading only, or outside every layer grid. */
export function shouldShowMapProbePopup(
  samples: MapProbeSample[],
  coordinate: [number, number],
  projectExtent: Extent
): boolean {
  if (samples.length === 0) return false
  if (!containsCoordinate(projectExtent, coordinate)) return false

  const available = samples.filter(s => !s.unavailable)
  if (available.length === 0) return false

  return available.some(s => !s.nearest)
}

/** Max row labels per column in the map probe table popup. */
const PROBE_TABLE_ROWS_PER_COLUMN = 8

function formatProbeTableCellValue(value: string): string {
  if (value === "—" || value.trim() === "") return "—"
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return value
  if (Number.isInteger(parsed)) return String(parsed)
  return parsed.toPrecision(4)
}

function chunkProbeTableRows<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

function appendProbeTableRowsList(
  parent: HTMLElement,
  rows: TableProbeMatch["rows"]
): void {
  const tableList = document.createElement("dl")
  tableList.className = "map-probe-popup__table-rows mb-0"
  for (const row of rows) {
    const tableRow = document.createElement("div")
    tableRow.className = "map-probe-popup__table-row"

    const rowLabel = document.createElement("dt")
    rowLabel.className = "map-probe-popup__table-row-label"
    rowLabel.title = row.rowLabel
    rowLabel.textContent = row.rowLabel

    const rowValue = document.createElement("dd")
    rowValue.className = "map-probe-popup__table-row-value"
    const formatted = formatProbeTableCellValue(row.value)
    rowValue.textContent = formatted
    rowValue.title = row.value !== formatted ? row.value : ""

    tableRow.appendChild(rowLabel)
    tableRow.appendChild(rowValue)
    tableList.appendChild(tableRow)
  }
  parent.appendChild(tableList)
}

export function renderProbePopupContent(
  container: HTMLElement,
  samples: MapProbeSample[],
  tableMatches: TableProbeMatch[] = []
): void {
  container.replaceChildren()

  if (samples.length === 0) {
    const empty = document.createElement("p")
    empty.className = "mb-0 text-muted"
    empty.textContent = "No model or saved dataset layers."
    container.appendChild(empty)
    return
  }

  const anyNearest = samples.some(s => s.nearest && !s.unavailable)
  if (anyNearest) {
    const hint = document.createElement("div")
    hint.className = "map-probe-popup__hint text-muted"
    hint.textContent = "Nearest cell (outside layer extent)"
    container.appendChild(hint)
  }

  const list = document.createElement("dl")
  list.className = "map-probe-popup__list mb-0"
  for (const sample of samples) {
    const row = document.createElement("div")
    row.className = "map-probe-popup__row"

    const dt = document.createElement("dt")
    dt.className = "map-probe-popup__name"
    dt.title = sample.layerName
    dt.textContent = sample.layerName

    const dd = document.createElement("dd")
    dd.className = `map-probe-popup__value${sample.unavailable ? " text-muted" : ""}`
    dd.textContent = sample.displayValue
    if (sample.valueColor) {
      dd.style.color = sample.valueColor
      dd.style.fontWeight = "600"
    }

    row.appendChild(dt)
    row.appendChild(dd)
    list.appendChild(row)
  }
  container.appendChild(list)

  for (const match of tableMatches) {
    const section = document.createElement("div")
    section.className = "map-probe-popup__table-section"

    const heading = document.createElement("div")
    heading.className = "map-probe-popup__table-heading"
    heading.textContent = `${match.tableName} — ${match.columnLabel}`
    section.appendChild(heading)

    if (match.rows.length === 0) {
      const empty = document.createElement("p")
      empty.className = "map-probe-popup__table-empty mb-0 text-muted"
      empty.textContent = "No rows in this table."
      section.appendChild(empty)
    } else {
      const chunks = chunkProbeTableRows(match.rows, PROBE_TABLE_ROWS_PER_COLUMN)
      const columnsWrap = document.createElement("div")
      columnsWrap.className = "map-probe-popup__table-columns"
      if (chunks.length > 1) {
        columnsWrap.classList.add("map-probe-popup__table-columns--multi")
      }
      for (const chunk of chunks) {
        appendProbeTableRowsList(columnsWrap, chunk)
      }
      section.appendChild(columnsWrap)
    }

    container.appendChild(section)
  }
}
